import React, { useEffect, useState } from 'react';
import {
	Flex,
	Box,
	Image,
	InputGroup,
	Input,
	InputLeftElement,
	InputRightElement,
	IconButton,
	Button,
	Badge,
	Popover,
	PopoverTrigger,
	PopoverContent,
	PopoverArrow,
	PopoverCloseButton,
	PopoverHeader,
	PopoverBody,
	Text,
	Spinner,
	VStack,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalFooter,
	ModalBody,
	ModalCloseButton,
	Stack,
	HStack,
	Divider,
	Avatar,
	Wrap,
	WrapItem,
} from '@chakra-ui/react';
import { SearchIcon, AddIcon, MinusIcon } from '@chakra-ui/icons';
import {
	MdShoppingCart,
	MdAddShoppingCart,
	MdLogout,
	MdList,
	MdPerson,
} from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import logoImage from '../assets/logo.png';
import { logout } from '../redux/actions/loginActions';
import { getUserCart } from '../redux/actions/cartActions';
import { RootState } from '../redux/store';
import { AddItemToCart } from '../redux/actions/cartActions';
import { getSearchSuggestions } from '../redux/actions/searchActions';
import { CLEAR_SEARCH_SUGGESTIONS } from '../redux/actions/index';
import axios from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

const ProfileMenu = ({ name }: { name: string }) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	return (
		<Popover placement="bottom-end">
			<PopoverTrigger>
				<Button variant="unstyled" display="flex" alignItems="center" gap={2} px={2} _hover={{ opacity: 0.85 }}>
					<Avatar size="sm" name={name} bg="green.300" color="gray.900" fontSize="xs" fontWeight="bold" />
					<Box display={{ base: 'none', md: 'block' }} textAlign="left">
						<Text fontSize="xs" color="whiteAlpha.700" lineHeight="1">Welcome</Text>
						<Text fontSize="sm" color="white" fontWeight="semibold" lineHeight="1.3">
							{name.split(' ')[0]}
						</Text>
					</Box>
				</Button>
			</PopoverTrigger>
			<PopoverContent boxShadow="2xl" borderRadius="xl" w="210px" border="none" overflow="hidden">
				<PopoverArrow />
				<PopoverCloseButton />
				<Box bg="linear-gradient(135deg, #0f3460 0%, #16213e 100%)" px={4} py={3}>
					<Text fontWeight="bold" color="white" fontSize="sm">{name}</Text>
					<Text fontSize="xs" color="whiteAlpha.700">Facility Account</Text>
				</Box>
				<PopoverBody p={2}>
					<VStack align="stretch" spacing={0}>
						<Button
							leftIcon={<MdPerson />}
							colorScheme="blue"
							variant="ghost"
							justifyContent="flex-start"
							size="sm"
							borderRadius="lg"
							onClick={() => navigate('/profile')}
							_hover={{ bg: 'blue.50' }}
						>
							My Profile
						</Button>
						<Button
							leftIcon={<MdList />}
							colorScheme="blue"
							variant="ghost"
							justifyContent="flex-start"
							size="sm"
							borderRadius="lg"
							onClick={() => navigate('/order')}
							_hover={{ bg: 'blue.50' }}
						>
							My Orders
						</Button>
						<Divider my={1} />
						<Button
							leftIcon={<MdLogout />}
							colorScheme="red"
							variant="ghost"
							justifyContent="flex-start"
							size="sm"
							borderRadius="lg"
							onClick={() => dispatch(logout())}
							_hover={{ bg: 'red.50' }}
						>
							Logout
						</Button>
					</VStack>
				</PopoverBody>
			</PopoverContent>
		</Popover>
	);
};

const Navbar = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [searchQuery, setSearchQuery] = useState('');
	const location = useLocation();
	const [selectedProduct, setSelectedProduct] = useState<any>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalQuantity, setModalQuantity] = useState(1);
	const [searchVariants, setSearchVariants] = useState<any[]>([]);
	const [searchVariantsLoading, setSearchVariantsLoading] = useState(false);
	const [selectedPerType, setSelectedPerType] = useState<Record<string, number>>({});

	const { cart, userDetails, search, isAuthenticated } = useSelector(
		(state: RootState) => ({
			cart: state.cart.items,
			userDetails: state.auth.userDetails,
			search: state.search,
			isAuthenticated: state.auth.isAuthenticated,
		})
	);

	useEffect(() => {
		dispatch(getUserCart());
	}, [dispatch]);

	useEffect(() => {
		const handler = setTimeout(() => {
			if (searchQuery.trim().length >= 3) {
				dispatch(getSearchSuggestions(searchQuery));
			} else {
				dispatch({ type: CLEAR_SEARCH_SUGGESTIONS });
			}
		}, 300);
		return () => clearTimeout(handler);
	}, [searchQuery, dispatch]);

	const goHome = () => {
		if (location.pathname === '/store') window.location.reload();
		else navigate('/store');
	};

	const handleSearch = (e: React.FormEvent) => e.preventDefault();

	// Derived variant state — only use variants priced for this facility
	const availableSearchVariants = searchVariants.filter((v) => v.final_price && parseFloat(v.final_price) > 0);
	const attrTypes: string[] = availableSearchVariants.length > 0
		? [...new Set(availableSearchVariants.flatMap((v) => v.attributes.map((a: any) => a.attribute_name)))]
		: [];
	const allSelected = attrTypes.length > 0 && attrTypes.every((t) => selectedPerType[t] != null);
	const matchedVariant = allSelected
		? availableSearchVariants.find((v) =>
			attrTypes.every((t) =>
				v.attributes.find((a: any) => a.attribute_name === t && a.attribute_id === selectedPerType[t])
			)
		)
		: null;
	const hasVariants = availableSearchVariants.length > 0;
	const displayModalPrice = matchedVariant
		? Number(matchedVariant.final_price)
		: Number(selectedProduct?.final_price ?? 0);
	const selectedVariantOutOfStock = matchedVariant ? !!matchedVariant.out_of_stock : false;
	const isOutOfStock = !!selectedProduct?.out_of_stock || selectedVariantOutOfStock;

	const handleSuggestionClick = async (product: any) => {
		setSelectedProduct(product);
		setModalQuantity(1);
		setSelectedPerType({});
		setSearchVariants([]);
		setIsModalOpen(true);
		dispatch({ type: CLEAR_SEARCH_SUGGESTIONS });
		setSearchQuery('');
		try {
			setSearchVariantsLoading(true);
			const res = await axios.get(BASE_URL + '/api/product-variants/' + product.product_id, {
				headers: { 'x-auth-token': localStorage.getItem('x-auth-token') || '' },
			});
			setSearchVariants(Array.isArray(res.data) ? res.data : []);
		} catch { setSearchVariants([]); }
		finally { setSearchVariantsLoading(false); }
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
		setSelectedProduct(null);
		setModalQuantity(1);
		setSelectedPerType({});
		setSearchVariants([]);
	};

	const handleAddToCartFromModal = () => {
		if (isOutOfStock) return;
		if (selectedProduct) {
			dispatch(AddItemToCart({
				...selectedProduct,
				quantity: modalQuantity,
				variant_id: matchedVariant?.variant_id || null,
			}));
			handleCloseModal();
		}
	};

	const cartCount = cart?.reduce((sum: number, item: any) => sum + (item.quantity || 1), 0) ?? 0;

	return (
		<Flex
			as="nav"
			align="center"
			justify="space-between"
			px={{ base: 4, md: 6 }}
			py={3}
			bg="linear-gradient(135deg, #0f3460 0%, #16213e 100%)"
			boxShadow="0 2px 20px rgba(0,0,0,0.3)"
			position="sticky"
			top="0"
			zIndex="999"
			gap={3}
		>
			{/* Logo */}
			<Box
				flexShrink={0}
				cursor="pointer"
				onClick={goHome}
				bg="white"
				borderRadius="lg"
				px={3}
				py={1.5}
				transition="all 0.2s"
				_hover={{ opacity: 0.9, boxShadow: '0 0 0 2px rgba(72,187,120,0.6)' }}
			>
				<Image src={logoImage} alt="Logo" h={{ base: '24px', md: '36px' }} />
			</Box>

			{/* Search Bar */}
			<Box position="relative" flex={1} maxW={{ base: '160px', sm: '280px', md: '440px' }} minW={0}>
				<form onSubmit={handleSearch}>
					<InputGroup>
						<InputLeftElement pointerEvents="none">
							<SearchIcon color="gray.400" boxSize={3.5} />
						</InputLeftElement>
						<Input
							placeholder="Search products..."
							size="md"
							borderRadius="full"
							bg="whiteAlpha.100"
							color="white"
							border="1px solid"
							borderColor="whiteAlpha.200"
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							_placeholder={{ color: 'whiteAlpha.500' }}
							_hover={{ bg: 'whiteAlpha.200', borderColor: 'whiteAlpha.400' }}
							_focus={{ bg: 'white', color: 'gray.800', borderColor: 'green.300', boxShadow: '0 0 0 2px rgba(72,187,120,0.3)', _placeholder: { color: 'gray.400' } }}
							transition="all 0.2s"
						/>
						<InputRightElement>
							<IconButton
								type="submit"
								aria-label="Search"
								icon={<SearchIcon />}
								variant="ghost"
								color="whiteAlpha.700"
								size="sm"
								_hover={{ color: 'white' }}
							/>
						</InputRightElement>
					</InputGroup>
				</form>

				{/* Search Suggestions Dropdown */}
				{searchQuery.length >= 3 && (
					<Box
						position="absolute"
						top="calc(100% + 8px)"
						left="0"
						right="0"
						bg="white"
						boxShadow="xl"
						borderRadius="xl"
						zIndex="1000"
						overflow="hidden"
						border="1px solid"
						borderColor="gray.100"
					>
						{search.loading && (
							<Flex justify="center" p={4}>
								<Spinner size="sm" color="blue.500" />
							</Flex>
						)}
						{!search.loading && search.suggestions?.length > 0 && (
							<VStack align="stretch" spacing={0} maxH="320px" overflowY="auto">
								{search.suggestions.map((product: any) => (
									<HStack
										key={product.product_id}
										px={3}
										py={2.5}
										cursor="pointer"
										_hover={{ bg: 'blue.50' }}
										transition="background 0.15s"
										onClick={() => handleSuggestionClick(product)}
									>
										<Box w="36px" h="36px" borderRadius="md" bg="gray.50" flexShrink={0} display="flex" alignItems="center" justifyContent="center" overflow="hidden">
											<Image src={`${BASE_URL}/api/image?filename=${product.image}`} boxSize="30px" objectFit="contain" />
										</Box>
										<Box flex={1} minW={0}>
											<Text fontSize="sm" fontWeight="medium" color="gray.800" noOfLines={1}>{product.name}</Text>
											{product.out_of_stock ? <Badge colorScheme="red" fontSize="2xs">Out of Stock</Badge> : null}
											{isAuthenticated && product.final_price ? (
												<Text fontSize="xs" color="blue.600" fontWeight="semibold">${Number(product.final_price).toFixed(2)}</Text>
											) : !isAuthenticated ? (
												<Text fontSize="xs" color="gray.500" fontWeight="semibold">Contact for price</Text>
											) : null}
										</Box>
									</HStack>
								))}
							</VStack>
						)}
						{!search.loading && search.suggestions?.length === 0 && !search.error && (
							<Text color="gray.500" p={4} fontSize="sm" textAlign="center">No results found.</Text>
						)}
					</Box>
				)}
			</Box>

			{/* Right: Auth + Cart */}
			<Flex align="center" gap={{ base: 1, md: 2 }} flexShrink={0}>
				{isAuthenticated ? (
					<>
						<Button
							leftIcon={<MdList />}
							variant="ghost"
							size="sm"
							color="whiteAlpha.800"
							fontWeight="medium"
							borderRadius="lg"
							display={{ base: 'none', md: 'flex' }}
							_hover={{ bg: 'whiteAlpha.200', color: 'green.300' }}
							onClick={() => navigate('/order')}
						>
							Orders
						</Button>
						<Button
							leftIcon={<MdPerson />}
							variant="ghost"
							size="sm"
							color="whiteAlpha.800"
							fontWeight="medium"
							borderRadius="lg"
							display={{ base: 'none', md: 'flex' }}
							_hover={{ bg: 'whiteAlpha.200', color: 'green.300' }}
							onClick={() => navigate('/profile')}
						>
							Profile
						</Button>
						<ProfileMenu name={userDetails?.name || 'User'} />
					</>
				) : (
					<Button
						colorScheme="green"
						size="sm"
						borderRadius="full"
						px={5}
						fontWeight="semibold"
						onClick={() => navigate('/login')}
						bg="green.400"
						color="white"
						_hover={{ bg: 'green.300' }}
					>
						Login
					</Button>
				)}

				{/* Cart */}
				<Box position="relative">
					<IconButton
						aria-label="Shopping Cart"
						icon={<MdShoppingCart />}
						variant="ghost"
						fontSize="22px"
						color="whiteAlpha.800"
						onClick={() => navigate('/cart')}
						_hover={{ bg: 'whiteAlpha.200', color: 'green.300' }}
						borderRadius="full"
					/>
					{cartCount > 0 && (
						<Badge
							position="absolute"
							top="-1"
							right="-1"
							borderRadius="full"
							bg="green.400"
							color="gray.900"
							px={1.5}
							fontSize="2xs"
							fontWeight="bold"
							boxShadow="md"
							minW="18px"
							textAlign="center"
						>
							{cartCount}
						</Badge>
					)}
				</Box>
			</Flex>

			{/* Quick-view Modal from Search */}
			{selectedProduct && (
				<Modal isOpen={isModalOpen} onClose={handleCloseModal} isCentered size={{ base: 'full', md: 'md' }}>
					<ModalOverlay bg="blackAlpha.600" />
					<ModalContent borderRadius="2xl" overflow="hidden">
						<Box bg="gray.50" h="220px" display="flex" alignItems="center" justifyContent="center">
							<Image
								src={`${BASE_URL}/api/image?filename=${selectedProduct.image}`}
								alt={selectedProduct.name}
								maxH="190px"
								maxW="75%"
								objectFit="contain"
							/>
						</Box>
						<ModalHeader pb={1}>{selectedProduct.name}</ModalHeader>
						<ModalCloseButton />
						<ModalBody pt={0}>
							{(() => {
								const isNotSold = Number(selectedProduct.final_price) === 0 && availableSearchVariants.length === 0;
								return (
									<>
										<Text fontSize="sm" color="gray.600" mb={3}>{selectedProduct.description}</Text>
										{isNotSold && isAuthenticated ? (
											<Text fontSize="lg" fontWeight="semibold" color="gray.400">Not sold at your facility</Text>
										) : !isAuthenticated ? (
											<Text fontSize="lg" fontWeight="semibold" color="gray.500">Contact for price</Text>
										) : (
											<Text color="blue.600" fontSize="2xl" fontWeight="bold">${displayModalPrice.toFixed(2)}</Text>
										)}
										{selectedProduct.unit && selectedProduct.quantity_per_unit && (
											<Stack spacing={0} fontSize="sm" color="gray.500" mt={1}>
												<Text>Sold in <strong>{selectedProduct.unit}s</strong> · <strong>{selectedProduct.quantity_per_unit}</strong> per {selectedProduct.unit}</Text>
											</Stack>
										)}

										{/* Variant Selectors */}
										{searchVariantsLoading && <Spinner size="sm" color="blue.400" mt={2} />}
										{!searchVariantsLoading && attrTypes.map((typeName) => {
											const options = availableSearchVariants.reduce((acc: any[], v) => {
												const a = v.attributes.find((a: any) => a.attribute_name === typeName);
												if (a && !acc.find((x) => x.attribute_id === a.attribute_id)) acc.push(a);
												return acc;
											}, []);
											return (
												<Box key={typeName} mt={3}>
													<Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={2}>{typeName}</Text>
													<Wrap spacing={2}>
														{options.map((opt) => (
															<WrapItem key={opt.attribute_id}>
																<Button
																	size="sm"
																	borderRadius="lg"
																	variant={selectedPerType[typeName] === opt.attribute_id ? 'solid' : 'outline'}
																	colorScheme={selectedPerType[typeName] === opt.attribute_id ? 'blue' : 'gray'}
																	onClick={() => setSelectedPerType((prev) => ({ ...prev, [typeName]: opt.attribute_id }))}
																>
																	{opt.attribute_value}
																</Button>
															</WrapItem>
														))}
													</Wrap>
												</Box>
											);
										})}
										{!searchVariantsLoading && hasVariants && !allSelected && (
											<Text fontSize="xs" color="orange.500" mt={2}>Please select all options before adding to cart.</Text>
										)}

										{!isNotSold && isAuthenticated && (
											<HStack mt={4} spacing={3} align="center">
												<Text fontSize="sm" fontWeight="medium" color="gray.600">Quantity</Text>
												<HStack spacing={0} border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
													<IconButton
														aria-label="Decrease"
														icon={<MinusIcon boxSize={2.5} />}
														size="sm"
														variant="ghost"
														onClick={() => setModalQuantity((q) => Math.max(1, q - 1))}
														isDisabled={modalQuantity <= 1}
														h={9} w={9}
													/>
													<Text px={4} fontSize="md" fontWeight="semibold">{modalQuantity}</Text>
													<IconButton
														aria-label="Increase"
														icon={<AddIcon boxSize={2.5} />}
														size="sm"
														variant="ghost"
														onClick={() => setModalQuantity((q) => q + 1)}
														h={9} w={9}
													/>
												</HStack>
											</HStack>
										)}
									</>
								);
							})()}
						</ModalBody>
						<ModalFooter gap={2}>
							{!isAuthenticated ? (
								<Button
									colorScheme="green"
									borderRadius="xl"
									flex={1}
									fontWeight="bold"
									onClick={() => {
										handleCloseModal();
										navigate('/contact');
									}}
								>
									Contact for price
								</Button>
							) : isOutOfStock ? (
								<Button colorScheme="red" borderRadius="xl" flex={1} fontWeight="bold" isDisabled>
									Out of Stock
								</Button>
							) : Number(selectedProduct.final_price) === 0 ? (
								<Button colorScheme="gray" borderRadius="xl" flex={1} fontWeight="bold" isDisabled>
									Not Available at Your Facility
								</Button>
							) : (
								<Button
									colorScheme="blue"
									leftIcon={<MdAddShoppingCart />}
									onClick={handleAddToCartFromModal}
									borderRadius="xl"
									flex={1}
									fontWeight="bold"
									isDisabled={hasVariants && !allSelected}
								>
									Add {modalQuantity > 1 ? `${modalQuantity} ` : ''}to Cart — ${(displayModalPrice * modalQuantity).toFixed(2)}
								</Button>
							)}
							<Button variant="ghost" onClick={handleCloseModal} borderRadius="xl">Close</Button>
						</ModalFooter>
					</ModalContent>
				</Modal>
			)}
		</Flex>
	);
};

export default Navbar;
